import { useEffect, useMemo, useState } from 'react'

import {
  type EpisodeGenerateQueueState,
  type EpisodeQueuePageStatus,
} from '../../../../lib/comicEpisodeGenerateQueue'
import { episodeQueueSummaryStatClass } from '../../../../lib/episodeQueueTimelineTheme'
import { HorizontalChipStrip } from '../../../HorizontalChipStrip'
import { ThemedImage } from '../../../ThemedImage'
import { ComicEpisodeGenerateQueueSummary } from '../../../comic/ComicEpisodeGenerateQueueSummary'
import { comicPanelRoundSm } from '../../../comic/comicPanelTokens'

type Props = {
  queue: EpisodeGenerateQueueState
  angular: boolean
  onStepClick?: (pageId: string, status: EpisodeQueuePageStatus) => void
}

function pageChipClass(angular: boolean, selected: boolean): string {
  return [
    'glyphicai-dynamic-island-comic-page-chip shrink-0 px-2.5 py-1.5 text-left transition-[box-shadow,background-color]',
    comicPanelRoundSm(angular),
    selected
      ? 'bg-[color-mix(in_srgb,var(--accent)_7%,var(--surface-muted))] shadow-[inset_0_0_0_2px_var(--accent)]'
      : 'bg-surface-muted/55 shadow-[inset_0_0_0_1px_var(--line)] hover:shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--accent)_45%,var(--line))]',
  ].join(' ')
}

function pageStatusTone(status: EpisodeQueuePageStatus): string {
  if (status === 'done') return episodeQueueSummaryStatClass('success')
  if (status === 'failed') return episodeQueueSummaryStatClass('failed')
  if (status === 'awaiting_selection') return episodeQueueSummaryStatClass('awaiting')
  return episodeQueueSummaryStatClass('default')
}

function pageStatusShort(status: EpisodeQueuePageStatus): string {
  if (status === 'done') return '完成'
  if (status === 'failed') return '失败'
  if (status === 'awaiting_selection') return '待选'
  if (status === 'cancelled') return '取消'
  if (status === 'running') return '生成中'
  return '排队'
}

function ComicQueuePagePreview({
  queue,
  pageId,
  angular,
  onOpen,
}: {
  queue: EpisodeGenerateQueueState
  pageId: string
  angular: boolean
  onOpen?: () => void
}) {
  const status = queue.statuses[pageId] ?? 'pending'
  const previews = queue.pagePreviewUrls?.[pageId] ?? []
  const title = queue.pageTitles[pageId] ?? pageId
  const frameClass = [
    'overflow-hidden bg-surface-muted/60',
    comicPanelRoundSm(angular),
    onOpen ? 'cursor-pointer' : 'cursor-default',
    'shadow-[inset_0_0_0_1px_var(--line)]',
  ].join(' ')

  if (previews.length > 0) {
    return (
      <div
        className={[
          'glyphicai-dynamic-island-comic-page-preview__grid',
          previews.length > 1 ? 'glyphicai-dynamic-island-comic-page-preview__grid--multi' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {previews.map((src, index) => (
          <button key={`${pageId}-${index}`} type="button" className={frameClass} onClick={onOpen}>
            <ThemedImage
              src={src}
              alt={`${title} 预览 ${index + 1}`}
              className="h-full w-full object-cover"
            />
          </button>
        ))}
      </div>
    )
  }

  return (
    <div className="glyphicai-dynamic-island-comic-page-preview__empty flex flex-col items-center justify-center gap-1.5 py-12 text-center">
      <p className="text-xs font-semibold text-ink">{title}</p>
      <p className={`text-[11px] ${pageStatusTone(status)}`}>{pageStatusShort(status)}</p>
      {status === 'pending' || status === 'running' ? (
        <p className="text-[10px] text-ink-soft">生成完成后将在此显示预览</p>
      ) : onOpen ? (
        <button
          type="button"
          className="text-[11px] font-semibold text-accent underline-offset-2 hover:underline"
          onClick={onOpen}
        >
          在编辑器中查看
        </button>
      ) : null}
    </div>
  )
}

export function DynamicIslandComicQueueDetailView({ queue, angular, onStepClick }: Props) {
  const defaultPageId = queue.currentPageId ?? queue.pageIds[0] ?? null
  const [selectedPageId, setSelectedPageId] = useState<string | null>(defaultPageId)

  useEffect(() => {
    setSelectedPageId((prev) => {
      if (prev && queue.pageIds.includes(prev)) return prev
      return queue.currentPageId ?? queue.pageIds[0] ?? null
    })
  }, [queue.currentPageId, queue.pageIds])

  const selectedPage = selectedPageId ?? queue.pageIds[0] ?? null
  const selectedStatus = selectedPage ? (queue.statuses[selectedPage] ?? 'pending') : 'pending'

  const pageChips = useMemo(
    () =>
      queue.pageIds.map((pageId, index) => ({
        pageId,
        label: `第 ${index + 1} 页`,
        status: queue.statuses[pageId] ?? 'pending',
      })),
    [queue.pageIds, queue.statuses],
  )

  return (
    <div className="glyphicai-dynamic-island-detail-comic flex h-full min-h-0 flex-col">
      <div className="glyphicai-dynamic-island-detail-comic__sticky shrink-0 space-y-2">
        <ComicEpisodeGenerateQueueSummary queue={queue} angular={angular} compact />
        {pageChips.length > 0 ? (
          <HorizontalChipStrip
            className="glyphicai-dynamic-island-comic-page-strip"
            trackClassName="gap-1.5"
            aria-label="分页"
          >
            {pageChips.map((chip) => {
              const selected = chip.pageId === selectedPage
              return (
                <button
                  key={chip.pageId}
                  type="button"
                  className={pageChipClass(angular, selected)}
                  aria-pressed={selected}
                  onClick={() => setSelectedPageId(chip.pageId)}
                >
                  <span className="block text-[11px] font-bold leading-none text-ink">{chip.label}</span>
                  <span
                    className={[
                      'mt-1 block text-[9px] font-semibold leading-none',
                      pageStatusTone(chip.status),
                    ].join(' ')}
                  >
                    {pageStatusShort(chip.status)}
                  </span>
                </button>
              )
            })}
          </HorizontalChipStrip>
        ) : null}
      </div>

      {selectedPage ? (
        <div className="glyphicai-dynamic-island-detail-comic__scroll scroll-region min-h-0 flex-1 overflow-y-auto">
          <ComicQueuePagePreview
            queue={queue}
            pageId={selectedPage}
            angular={angular}
            onOpen={
              onStepClick ? () => onStepClick(selectedPage, selectedStatus) : undefined
            }
          />
        </div>
      ) : null}
    </div>
  )
}
